import { useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  useRoadmapItems,
  useCreateRoadmapItem,
  useUpdateRoadmapItem,
  useDeleteRoadmapItem,
  useReorderRoadmapItems,
  type RoadmapItemResponse,
} from "../../../lib/api/roadmap.api";

const STATUS_CONFIG: Record<string, { label: string; dot: string }> = {
  done: { label: "Disponible", dot: "bg-emerald-400" },
  "in-progress": { label: "En cours", dot: "bg-amber-400" },
  planned: { label: "Prévu", dot: "bg-ink-faint/40" },
};

const STATUSES = ["done", "in-progress", "planned"] as const;

const NEW_CATEGORY_VALUE = "__new__";

function CategoryPicker({ value, onChange, categories }: {
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly categories: readonly string[];
}) {
  const [creating, setCreating] = useState(false);
  const [newCat, setNewCat] = useState("");

  const handleSelect = (selected: string) => {
    if (selected === NEW_CATEGORY_VALUE) {
      setCreating(true);
    } else {
      onChange(selected);
    }
  };

  if (creating) {
    return (
      <div className="flex items-center gap-1.5">
        <input
          type="text"
          value={newCat}
          onChange={(e) => setNewCat(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && newCat.trim()) {
              onChange(newCat.trim());
              setCreating(false);
              setNewCat("");
            }
            if (e.key === "Escape") { setCreating(false); setNewCat(""); }
          }}
          className="px-2.5 py-1.5 bg-surface-alt border border-border/50 rounded-lg text-xs text-ink focus:outline-none focus:ring-1 focus:ring-accent/30 w-40"
          placeholder="Nom de la catégorie..."
          autoFocus
        />
        <button
          type="button"
          onClick={() => { if (newCat.trim()) { onChange(newCat.trim()); setCreating(false); setNewCat(""); } }}
          disabled={!newCat.trim()}
          className="text-[10px] text-accent hover:text-accent/80 disabled:opacity-40"
        >
          OK
        </button>
        <button
          type="button"
          onClick={() => { setCreating(false); setNewCat(""); }}
          className="text-[10px] text-ink-muted hover:text-ink"
        >
          ✕
        </button>
      </div>
    );
  }

  const isCustom = value && !categories.includes(value);
  const allOptions = isCustom ? [...categories, value].sort() : categories;

  return (
    <select
      value={value || ""}
      onChange={(e) => handleSelect(e.target.value)}
      className="px-2.5 py-1.5 bg-surface-alt border border-border/50 rounded-lg text-xs text-ink focus:outline-none focus:ring-1 focus:ring-accent/30"
    >
      <option value="" disabled>Catégorie...</option>
      {allOptions.map((cat) => (
        <option key={cat} value={cat}>{cat}</option>
      ))}
      <option value={NEW_CATEGORY_VALUE}>+ Nouvelle catégorie</option>
    </select>
  );
}

interface SortableRoadmapRowProps {
  readonly item: RoadmapItemResponse;
  readonly categories: readonly string[];
}

function SortableRoadmapRow({ item, categories }: SortableRoadmapRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(item.title);
  const [description, setDescription] = useState(item.description);
  const [category, setCategory] = useState(item.category);
  const updateItem = useUpdateRoadmapItem();
  const deleteItem = useDeleteRoadmapItem();
  const dot = STATUS_CONFIG[item.status]?.dot ?? STATUS_CONFIG["planned"].dot;

  const handleSave = () => {
    updateItem.mutate({ id: item.id, title, description, category }, {
      onSuccess: () => setEditing(false),
    });
  };

  return (
    <div ref={setNodeRef} style={style} className={`group rounded-lg border transition-colors ${
      editing ? "border-accent/30 bg-surface" : "border-transparent hover:bg-surface/50"
    }`}>
      <div
        className="flex items-center gap-3 px-3 py-2.5 cursor-pointer"
        onClick={() => { if (!editing) setEditing(true); }}
      >
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="shrink-0 p-1 text-ink-faint/40 hover:text-ink-muted cursor-grab active:cursor-grabbing"
          title="Glisser pour réorganiser"
        >
          <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
            <circle cx="9" cy="6" r="1.5" />
            <circle cx="15" cy="6" r="1.5" />
            <circle cx="9" cy="12" r="1.5" />
            <circle cx="15" cy="12" r="1.5" />
            <circle cx="9" cy="18" r="1.5" />
            <circle cx="15" cy="18" r="1.5" />
          </svg>
        </button>
        <span className={`w-2 h-2 rounded-full shrink-0 ${dot}`} />
        <span className="text-sm text-ink font-medium flex-1 truncate">{item.title}</span>
        <span className="text-[10px] text-ink-faint bg-surface-alt/60 px-1.5 py-0.5 rounded shrink-0">
          {item.category}
        </span>
        {item.voteCount > 0 && (
          <span className="flex items-center gap-0.5 text-[10px] text-accent font-medium shrink-0">
            {item.voteCount}
            <svg viewBox="0 0 24 24" className="w-3 h-3" fill="currentColor" stroke="none">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
          </span>
        )}
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setEditing((v) => !v); }}
            className="p-0.5 text-ink-faint hover:text-ink transition-colors"
            title="Modifier"
          >
            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
            </svg>
          </button>
        </div>
      </div>

      {editing && (
        <div className="px-3 pb-3 flex flex-col gap-2">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-2.5 py-1.5 bg-surface-alt border border-border/50 rounded-lg text-sm text-ink focus:outline-none focus:ring-1 focus:ring-accent/30"
            placeholder="Titre"
          />
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="w-full px-2.5 py-1.5 bg-surface-alt border border-border/50 rounded-lg text-sm text-ink resize-none focus:outline-none focus:ring-1 focus:ring-accent/30"
            placeholder="Description"
          />
          <div className="flex items-center gap-2">
            <CategoryPicker value={category} onChange={setCategory} categories={categories} />
            <div className="flex gap-1 shrink-0 ml-auto">
              {STATUSES.map((s) => (
                <button
                  key={s}
                  type="button"
                  disabled={item.status === s}
                  onClick={() => updateItem.mutate({ id: item.id, status: s })}
                  className={`px-2 py-1 text-[10px] rounded-md transition-colors ${
                    item.status === s
                      ? "bg-accent text-white"
                      : "text-ink-muted hover:bg-surface-alt border border-border/40"
                  }`}
                >
                  {STATUS_CONFIG[s]?.label ?? s}
                </button>
              ))}
            </div>
          </div>
          <div className="flex justify-between items-center pt-1">
            <button
              type="button"
              onClick={() => { if (confirm("Supprimer cet item ?")) deleteItem.mutate(item.id); }}
              className="text-[11px] text-red-500/70 hover:text-red-500 transition-colors"
            >
              Supprimer
            </button>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="px-3 py-1 text-[11px] text-ink-muted hover:text-ink transition-colors"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={updateItem.isPending}
                className="px-3 py-1 text-[11px] bg-accent text-white rounded-md hover:bg-accent/90 transition-colors"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminRoadmapTab() {
  const { data: items, isLoading } = useRoadmapItems();
  const createItem = useCreateRoadmapItem();
  const reorderItems = useReorderRoadmapItems();
  const [showForm, setShowForm] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [newStatus, setNewStatus] = useState<"done" | "in-progress" | "planned">("planned");

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    // Trouver les indices
    const oldIndex = items?.findIndex((item) => item.id === active.id) ?? -1;
    const newIndex = items?.findIndex((item) => item.id === over.id) ?? -1;

    if (oldIndex === -1 || newIndex === -1 || !items) return;

    // Créer le nouvel ordre
    const newOrder = [...items];
    const [removed] = newOrder.splice(oldIndex, 1);
    newOrder.splice(newIndex, 0, removed);

    // Envoyer les IDs dans le nouvel ordre
    reorderItems.mutate(newOrder.map((item) => item.id));
  };

  const handleCreate = () => {
    if (!newTitle.trim() || !newDescription.trim() || !newCategory.trim()) return;
    createItem.mutate(
      { title: newTitle.trim(), description: newDescription.trim(), category: newCategory.trim(), status: newStatus },
      {
        onSuccess: () => {
          setNewTitle("");
          setNewDescription("");
          setNewCategory("");
          setNewStatus("planned");
          setShowForm(false);
        },
      },
    );
  };

  if (isLoading) return <p className="text-ink-muted text-sm">Chargement...</p>;

  const categories = [...new Set(items?.map((i) => i.category) ?? [])].sort();

  const grouped = {
    "in-progress": items?.filter((i) => i.status === "in-progress") ?? [],
    planned: items?.filter((i) => i.status === "planned") ?? [],
    done: items?.filter((i) => i.status === "done") ?? [],
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <p className="text-sm font-semibold text-ink">{items?.length ?? 0} items</p>
          <div className="flex items-center gap-3">
            {Object.entries(grouped).map(([status, statusItems]) => (
              <div key={status} className="flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${STATUS_CONFIG[status]?.dot}`} />
                <span className="text-[10px] text-ink-muted">{statusItems.length}</span>
              </div>
            ))}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShowForm((v) => !v)}
          className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
            showForm
              ? "text-ink-muted hover:text-ink"
              : "bg-accent text-white hover:bg-accent/90"
          }`}
        >
          {showForm ? "Annuler" : "+ Ajouter"}
        </button>
      </div>

      {showForm && (
        <div className="bg-surface rounded-xl p-3 flex flex-col gap-2 border border-accent/20">
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            className="w-full px-2.5 py-1.5 bg-surface-alt border border-border/50 rounded-lg text-sm text-ink focus:outline-none focus:ring-1 focus:ring-accent/30"
            placeholder="Ex: Intégration Notion"
            autoFocus
          />
          <textarea
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            rows={2}
            className="w-full px-2.5 py-1.5 bg-surface-alt border border-border/50 rounded-lg text-sm text-ink resize-none focus:outline-none focus:ring-1 focus:ring-accent/30"
            placeholder="Ex: Synchronisez vos tâches Palnia avec Notion en temps réel."
          />
          <div className="flex items-center gap-2">
            <CategoryPicker value={newCategory} onChange={setNewCategory} categories={categories} />
            <div className="flex gap-1 shrink-0 ml-auto">
              {STATUSES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setNewStatus(s)}
                  className={`px-2 py-1 text-[10px] rounded-md transition-colors ${
                    newStatus === s
                      ? "bg-accent text-white"
                      : "text-ink-muted hover:bg-surface-alt border border-border/40"
                  }`}
                >
                  {STATUS_CONFIG[s]?.label}
                </button>
              ))}
            </div>
          </div>
          <button
            type="button"
            onClick={handleCreate}
            disabled={createItem.isPending || !newTitle.trim() || !newDescription.trim() || !newCategory.trim()}
            className="self-end px-4 py-1.5 text-xs bg-accent text-white rounded-lg hover:bg-accent/90 disabled:opacity-40 transition-colors"
          >
            Créer
          </button>
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        {Object.entries(grouped).map(([status, statusItems]) => (
          statusItems.length > 0 && (
            <div key={status} className="flex flex-col gap-0.5">
              <div className="flex items-center gap-2 mb-1 px-1">
                <span className={`w-2 h-2 rounded-full ${STATUS_CONFIG[status]?.dot}`} />
                <h3 className="text-[11px] font-semibold text-ink-muted uppercase tracking-wider">
                  {STATUS_CONFIG[status]?.label} ({statusItems.length})
                </h3>
              </div>
              <SortableContext
                items={statusItems.map((item) => item.id)}
                strategy={verticalListSortingStrategy}
              >
                {statusItems.map((item) => (
                  <SortableRoadmapRow
                    key={item.id}
                    item={item}
                    categories={categories}
                  />
                ))}
              </SortableContext>
            </div>
          )
        ))}
      </DndContext>
    </div>
  );
}
